import { useState, useEffect } from 'react';
import { useRegion } from '@/contexts/RegionContext';

export interface RegionalPageContent {
  title: string;
  htmlContent: string;
  seoTitle?: string;
  seoDescription?: string;
  [key: string]: any;
}

export function useRegionalPageContent(pageId: string) {
  const { regionCode } = useRegion();
  const [content, setContent] = useState<RegionalPageContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!regionCode || !pageId) return;
    
    let isMounted = true;
    setLoading(true);
    
    
    const fetchContent = async () => {
      try {
        let finalData = null;
        const res = await fetch(`/api/public/regional-content?page=${encodeURIComponent(pageId)}&region=${encodeURIComponent(regionCode)}`);
        if (res.ok) {
          finalData = await res.json();
        }
        
        if (isMounted) {
          setContent(finalData as RegionalPageContent);
          
          if (finalData) {
            // Apply SEO dynamically
            if (finalData.seoTitle) {
              document.title = finalData.seoTitle;
            }
            if (finalData.seoDescription) {
              let metaDescription = document.querySelector('meta[name="description"]');
              if (!metaDescription) {
                metaDescription = document.createElement('meta');
                metaDescription.setAttribute('name', 'description');
                document.head.appendChild(metaDescription);
              }
              metaDescription.setAttribute('content', finalData.seoDescription);
            }
          }
        }
      } catch (e: any) {
        if (e?.message?.includes('offline') || e?.code?.includes('unavailable')) {
          console.warn(`Firestore offline: falling back to default content for ${pageId}`);
        } else {
          console.warn(`Error fetching regional content for ${pageId}:`, e);
        }
        if (isMounted) setContent(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchContent();
    
    return () => {
      isMounted = false;
    };
  }, [regionCode, pageId]);

  return { content, loading };
}
